import { useRouteContext } from '@/app/layouts/TenantLayout'
import { RouteAccessBoundary } from '@/components/access-control'
import { EmptyState } from '@/components/ui/empty-state'
import { type LabId, getLab, useLab } from '@/config/labs'
import { projectPath } from '@/lib/routeScope'
import { useIsAdmin } from '@/utils/permissions'
import { useNavigate } from '@tanstack/react-router'
import { FlaskConical } from 'lucide-react'
import type { ReactNode } from 'react'

interface LabBoundaryProps {
	id: LabId
	children: ReactNode
}

export function LabBoundary({ id, children }: LabBoundaryProps) {
	const enabled = useLab(id)
	const isAdmin = useIsAdmin()
	const navigate = useNavigate()
	const { orgId, tenantId } = useRouteContext()

	// Unwrapped on purpose: the page gates itself and keeps its create CTA for roles without read.
	if (enabled) {
		return <>{children}</>
	}

	const lab = getLab(id)
	const turnOn =
		isAdmin && orgId && tenantId
			? {
					label: 'Turn on in Modules',
					onClick: () => navigate({ to: projectPath(orgId, tenantId, '/settings/modules') }),
					dataTestId: `lab-boundary-${id}-turn-on`,
				}
			: undefined

	return (
		<RouteAccessBoundary resource={lab.resource}>
			<EmptyState
				icon={FlaskConical}
				title={`${lab.name} is in Labs`}
				description={isAdmin ? undefined : 'Ask a project admin to turn it on.'}
				action={turnOn}
				dataTestId={`lab-boundary-${id}`}
				containerHeight='100%'
				showBorder={false}
			/>
		</RouteAccessBoundary>
	)
}
